import { useEffect } from "react";

import { AnsweredQuestion, ApproveType, ChatMessage, DeleteQuestionComment, DeleteType, Message, ModeratorReply, QuestionBan, StatusUpdate } from "types/socket-messages";
import { useSocket } from "../../../../../../../../connection/socket";
import { LanguagesAbbr, PageModule, PaginatedQuestion, QuestionComment, SqsSocketTypes } from "../../../../../../../../types/working-model";

interface IUseQuestionsSocketsProps {
	session_uuid: string;
	language: LanguagesAbbr;
	module?: PageModule;
	singleLanguage: boolean;
	onMessage: (type: string, data: unknown) => void;
}

const useQuestionsSocketsAdmin = ({
	session_uuid,
	language,
	module,
	singleLanguage,
	onMessage,
}: IUseQuestionsSocketsProps): void => {
	const questionSocket = useSocket(singleLanguage ? `session-${session_uuid}` : `session-${session_uuid}-${language}`);
	const modSocket = useSocket(singleLanguage ? `session-${session_uuid}-mod` : `session-${session_uuid}-${language}-mod`);
	const generalSocket = useSocket(`session-${session_uuid}`);

	useEffect(() => {
		const handleQuestionMessage = (message: ChatMessage) => {
			message?.chat_messages?.forEach((msg: PaginatedQuestion) => {
				if (msg.module_id && msg.module_id === module?.id) {
					onMessage(SqsSocketTypes.QUESTION, msg);
				}
			});
		};

		const handleQuestionCommentMessage = (message: ChatMessage<QuestionComment> | ModeratorReply) => {
			const handleIncomingMessage = (msg: QuestionComment) => {
				if (module?.id) {
					onMessage(SqsSocketTypes.QUESTION_COMMENT, msg);
				}
			};

			// if message.data, then it's a moderator message
			if ('data' in message) {
				handleIncomingMessage(message.data);
			} else {
				// else it's a queue message
				message?.chat_messages?.forEach((msg) => {
					handleIncomingMessage(msg);
				});
			}
		};

		const handleQuestionLikeMessage = (message: ChatMessage) => {
			if (!module?.id) return;
			message?.chat_messages?.forEach((msg) => {
				onMessage(SqsSocketTypes.QUESTION_LIKE, msg);
			});
		};

		const handleQuestionUnlikeMessage = (message: ChatMessage) => {
			if (!module?.id) return;
			message?.chat_messages?.forEach((msg) => {
				onMessage(SqsSocketTypes.QUESTION_UNLIKE, msg);
			});
		};

		const updateQuestionStatus = (msg: Message<StatusUpdate>) => {
			if (!msg?.data || !module?.id) return;
			onMessage('question_status', msg);
		};

		const deleteQuestion = (msg: Message<DeleteType>) => {
			if (!msg?.data || !module?.id) return;
			onMessage('delete_question', msg);
		};

		const unDeleteQuestion = (msg: Message<ApproveType>) => {
			if (!msg?.data || !module?.id) return;
			onMessage('undelete_question', msg);
		};

		const updateQuestionCommentStatus = (msg: Message<StatusUpdate>) => {
			if (!msg?.data) return;
			onMessage('question_comment_status', msg);
		};

		const handleBan = (msg: Message<QuestionBan[]>) => {
			if (!msg?.data || !module?.id) return;
			onMessage('ban', msg);
		};

		const deleteQuestionComment = (msg: Message<DeleteQuestionComment>) => {
			if (!msg?.data) return;
			onMessage('delete_question_comment', msg);
		};

		const unDeleteQuestionComment = (msg: Message<QuestionComment>) => {
			if (!msg?.data) return;
			onMessage('undelete_question_comment', msg);
		};

		const handleBulkAnswer = (msg: Message<AnsweredQuestion>) => {
			if (!module?.id) return;
			onMessage('bulk_answer', msg);
		};

		// from lambda (regular + mod queue)
		questionSocket.addListener(SqsSocketTypes.QUESTION, handleQuestionMessage);
		questionSocket.addListener(SqsSocketTypes.QUESTION_COMMENT, handleQuestionCommentMessage);
		questionSocket.addListener(SqsSocketTypes.QUESTION_LIKE, handleQuestionLikeMessage);
		questionSocket.addListener(SqsSocketTypes.QUESTION_UNLIKE, handleQuestionUnlikeMessage);
		modSocket.addListener(SqsSocketTypes.QUESTION, handleQuestionMessage);
		modSocket.addListener(SqsSocketTypes.QUESTION_COMMENT, handleQuestionCommentMessage);
		modSocket.addListener(SqsSocketTypes.QUESTION_LIKE, handleQuestionLikeMessage);
		modSocket.addListener(SqsSocketTypes.QUESTION_UNLIKE, handleQuestionUnlikeMessage);
		// moderator updates
		questionSocket.addListener('approve_question', updateQuestionStatus);
		questionSocket.addListener('unapprove_question', updateQuestionStatus);
		questionSocket.addListener('delete_question', deleteQuestion);
		questionSocket.addListener('undelete_question', unDeleteQuestion);
		questionSocket.addListener('approve_question_comment', updateQuestionCommentStatus);
		questionSocket.addListener('unapprove_question_comment', updateQuestionCommentStatus);
		questionSocket.addListener('delete_question_comment', deleteQuestionComment);
		questionSocket.addListener('undelete_question_comment', unDeleteQuestionComment);
		questionSocket.addListener('question_answered_bulk', handleBulkAnswer);
		generalSocket.addListener('question_banned', handleBan);
		return () => {
			// remove socket listeners
			questionSocket.removeListener(SqsSocketTypes.QUESTION, handleQuestionMessage);
			questionSocket.removeListener(SqsSocketTypes.QUESTION_COMMENT, handleQuestionCommentMessage);
			questionSocket.removeListener(SqsSocketTypes.QUESTION_LIKE, handleQuestionLikeMessage);
			questionSocket.removeListener(SqsSocketTypes.QUESTION_UNLIKE, handleQuestionUnlikeMessage);
			modSocket.removeListener(SqsSocketTypes.QUESTION, handleQuestionMessage);
			modSocket.removeListener(SqsSocketTypes.QUESTION_COMMENT, handleQuestionCommentMessage);
			modSocket.removeListener(SqsSocketTypes.QUESTION_LIKE, handleQuestionLikeMessage);
			modSocket.removeListener(SqsSocketTypes.QUESTION_UNLIKE, handleQuestionUnlikeMessage);

			questionSocket.removeListener('approve_question', updateQuestionStatus);
			questionSocket.removeListener('unapprove_question', updateQuestionStatus);
			questionSocket.removeListener('delete_question', deleteQuestion);
			questionSocket.removeListener('undelete_question', unDeleteQuestion);
			questionSocket.removeListener('approve_question_comment', updateQuestionCommentStatus);
			questionSocket.removeListener('unapprove_question_comment', updateQuestionCommentStatus);
			questionSocket.removeListener('delete_question_comment', deleteQuestionComment);
			questionSocket.removeListener('undelete_question_comment', unDeleteQuestionComment);
			questionSocket.removeListener('question_answered_bulk', handleBulkAnswer);
			generalSocket.removeListener('question_banned', handleBan);

		};
	}, [generalSocket, modSocket, module?.id, onMessage, questionSocket]);
};

export default useQuestionsSocketsAdmin;
